import { t } from 'i18next';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import BackButton from '../../../components/BackButton';
import Loader from '../../../components/SplashScreen/loader';
import { RenderVisitList } from './components/renderPastVisit';
import VisitHeader from './visit-header';
import VisitForm from './visit-form';
import { getComplains, getDurations, getFrequencies, getMedicine, getSeverities, getPastVisits, getDiagnosis, getDose, getTiming, getTestRequired, getAdvice, getDietExercises, getGeneral, getCvs, getRs, getCns, getPa, getEnt, getAllVisits, createVisit, getVisitById, updateVisit, getTestDeadline } from '../../../redux/visit';
import { resetVisitDetails } from '../../../redux/visit/slice';
import LoadVisitModal from './components/loadVisitModal';
import { getTouchedArrayFields, getTouchedVitals, getTouchedObjectFields, visitInitialValues } from './composables';

const AddUpdateVisit = () => {
	const params = useParams();
	const perPage = 10;
	const { visitId } = useParams();
	const { pastVisits, allVisits, visitDetails, totalPastVisits } = useSelector(state => state.visits);
	const [loading, setLoading] = useState(false);
	const [visitPage, setVisitPage] = useState(1);
	const [isLoadVisits, setIsLoadVisits] = useState(false);
	const [selectedVisit, setSelectedVisit] = useState(null);
	const visitDetailsRef = useRef(null);
	const dispatch = useDispatch();
	const router = useNavigate();

	const formRef = useRef();

	const handleLoadVisits = () => {
		dispatch(getAllVisits({ pId: params.id, type: 'all', page: 1, per_page: perPage }));
		setIsLoadVisits(true);
	};

	const handleClearAll = () => {
		formRef.current.setTouched({}, false);
		formRef.current.resetForm({ values: visitInitialValues });
		formRef.current.setSubmitting(false);
		dispatch(resetVisitDetails());
	};

	const handleSelectVisit = (visit) => {
		setSelectedVisit(visit);
		setIsLoadVisits(false);
	};

	const handleEndConsultation = async() => {
		const formik = formRef.current;
		if (formik) {
			const errors = await formik.validateForm();

			const { touched: vitalsTouched, hasError: hasVitalError } = getTouchedVitals(errors);
			const { touched: complaintsTouched, hasError: hasComplaintError } = getTouchedArrayFields(errors, 'complaints');
			const { touched: diagnosisTouched, hasError: hasDiagnosisError } = getTouchedArrayFields(errors, 'diagnosis');
			const { touched: rxTouched, hasError: hasRxError } = getTouchedArrayFields(errors, 'rx');
			const { touched: referToTouched, hasError: hasReferToError } = getTouchedObjectFields(errors, 'referTo');

			const finalTouched = {
				...vitalsTouched,
				...complaintsTouched,
				...diagnosisTouched,
				...rxTouched,
				...referToTouched,
			};

			if (hasVitalError || hasComplaintError || hasDiagnosisError || hasRxError || hasReferToError) {
				formik.setTouched(finalTouched, true);
				return;
			}

			const { sysExamination } = formRef.current.values;

			const payload = {
				pId: params.id,
				vitals: formRef.current.values.vitals,
				systemicExamination: {
					generalId: Number(sysExamination.generalId) || undefined,
					cvsId: Number(sysExamination.cvsId) || undefined,
					rsId: Number(sysExamination.rsId) || undefined,
					cnsId: Number(sysExamination.cnsId) || undefined,
					paId: Number(sysExamination.paId) || undefined,
					entId: Number(sysExamination.entId) || undefined,
					generalName: sysExamination.generalName ? sysExamination.generalName : undefined,
					cvsName: sysExamination.cvsName ? sysExamination.cvsName : undefined,
					rsName: sysExamination.rsName ? sysExamination.rsName : undefined,
					cnsName: sysExamination.cnsName ? sysExamination.cnsName : undefined,
					paName: sysExamination.paName ? sysExamination.paName : undefined,
					entName: sysExamination.entName ? sysExamination.entName : undefined,
				},
				complaints: formRef.current.values.complaints.map((complaint) => ({
					id: complaint.existingVisitComplaintId ? Number(complaint.existingVisitComplaintId) : undefined,
					complaintId: Number(complaint.complaintId) || undefined,
					durationId: Number(complaint.durationId) || undefined,
					severityId: Number(complaint.severityId) || undefined,
					frequencyId: Number(complaint.frequencyId) || undefined,
					complaintName: complaint.complaintName ? complaint.complaintName : undefined,
					durationName: complaint.durationName ? complaint.durationName : undefined,
					severityName: complaint.severityName ? complaint.severityName : undefined,
					frequencyName: complaint.frequencyName ? complaint.frequencyName : undefined,
					date: complaint.date && complaint.date !== 'Invalid date' ? complaint.date : undefined,
				})).filter((d) => Object.values(d).some((v) => v !== undefined)),
				diagnosis: formRef.current.values.diagnosis.map((diagnosis) => ({
					id: diagnosis.existingVisitDiagnosisId ? Number(diagnosis.existingVisitDiagnosisId) : undefined,
					diagnosisId: Number(diagnosis.diagnosisId) || undefined,
					durationId: Number(diagnosis.durationId) || undefined,
					diagnosisName: diagnosis.diagnosisName ? diagnosis.diagnosisName : undefined,
					durationName: diagnosis.durationName ? diagnosis.durationName : undefined,
					date: diagnosis.date && diagnosis.date !== 'Invalid date' ? diagnosis.date : undefined,
				})).filter((d) => Object.values(d).some((v) => v !== undefined)),
				prescriptions: formRef.current.values.rx.map((rx) => ({
					id: rx.existingVisitMedicineId ? Number(rx.existingVisitMedicineId) : undefined,
					medicineId: Number(rx.medicineId) || undefined,
					doseId: Number(rx.doseId) || undefined,
					timingId: Number(rx.timingId) || undefined,
					frequencyId: Number(rx.frequencyId) || undefined,
					durationId: Number(rx.durationId) || undefined,
					medicineName: rx.medicineName ? rx.medicineName : undefined,
					doseName: rx.doseName ? rx.doseName : undefined,
					timingName: rx.timingName ? rx.timingName : undefined,
					frequencyName: rx.frequencyName ? rx.frequencyName : undefined,
					durationName: rx.durationName ? rx.durationName : undefined,
					notes: rx.note || undefined,
				})).filter((d) => Object.values(d).some((v) => v !== undefined)),
				advices: formRef.current.values.advice.map((adv) => ({
					adviceId: adv.adviceId ? Number(adv.adviceId) : undefined,
					adviceName: adv.adviceName ? adv.adviceName : undefined,
				})).filter((d) => Object.values(d).some((v) => v !== undefined)),
				testRequireds: formRef.current.values.testRequired.map((testRequired) => ({
					testRequiredId: Number(testRequired.testRequiredId) || undefined,
					testRequiredName: testRequired.testRequiredName || undefined,
				})).filter((d) => Object.values(d).some((v) => v !== undefined)),
				testDeadline: {
					testDurationId: Number(formRef.current.values.testDeadline.testDurationId) || undefined,
					testDeadlineId: Number(formRef.current.values.testDeadline.testDeadlineId) || undefined,
					testDeadlineName: formRef.current.values.testDeadline.testDeadlineName || undefined,
					testDeadlineDurationName: formRef.current.values.testDeadline.testDeadlineDurationName || undefined,
				},
				dietExercises: formRef.current.values.dietExercise.map((dietExercise) => ({
					dietExerciseId: Number(dietExercise.dietExerciseId) || undefined,
					dietExerciseName: dietExercise.dietExerciseName || undefined,
				})).filter((d) => Object.values(d).some((v) => v !== undefined)),
				nextVisit: {
					nextVisitDate: formRef.current.values.nextVisit.nextVisitDate ? formRef.current.values.nextVisit.nextVisitDate : '',
					nextVisit: formRef.current.values.nextVisit.nextVisit ? formRef.current.values.nextVisit.nextVisit : '',
					nextVisitType: formRef.current.values.nextVisit.period ? formRef.current.values.nextVisit.period.toLowerCase() : '',
				},
				referTo: {
					drName: formRef.current.values.referTo.drName,
					mobile: formRef.current.values.referTo.mobile,
					email: formRef.current.values.referTo.email,
				},
				history: formRef.current.values.history,
			};

			setLoading(true);
			try {
				if (visitId) {
					await dispatch(updateVisit({ visitId, payload })).unwrap();
				} else {
					await dispatch(createVisit(payload)).unwrap();
				}
				formRef.current.submitForm();
				formRef.current.resetForm();
				formRef.current.setFieldValue(visitInitialValues);
				formRef.current.setSubmitting(false);
				dispatch(resetVisitDetails());
				router(`/patients/visits/${params.id}`);
			} catch (err) {
				if (err) {
					setLoading(false);
					formRef.current.setSubmitting(false);
				}
			} finally {
				setLoading(false);
			}
		}
	};

	useEffect(() => {
		dispatch(getPastVisits({ pId: params.id, type: 'all', page: 1, per_page: 10 }));
		dispatch(getComplains({ per_page: 100, status: 'active' }));
		dispatch(getFrequencies({ per_page: 100 }));
		dispatch(getDurations({ per_page: 100 }));
		dispatch(getTestDeadline({ per_page: 100 }));
		dispatch(getSeverities({ per_page: 100 }));
		dispatch(getDiagnosis({ per_page: 100, status: 'active' }));
		dispatch(getMedicine({ per_page: 100, status: 'active' }));
		dispatch(getDose({ per_page: 100 }));
		dispatch(getTiming({ per_page: 100 }));
		dispatch(getTestRequired({ per_page: 100, status: 'active' }));
		dispatch(getAdvice({ per_page: 100, status: 'active' }));
		dispatch(getDietExercises({ per_page: 100, status: 'active' }));
		dispatch(getGeneral({ per_page: 100, status: 'active' }));
		dispatch(getCvs({ per_page: 100, status: 'active' }));
		dispatch(getRs({ per_page: 100, status: 'active' }));
		dispatch(getCns({ per_page: 100, status: 'active' }));
		dispatch(getPa({ per_page: 100, status: 'active' }));
		dispatch(getEnt({ per_page: 100, status: 'active' }));
	}, []);

	useEffect(() => {
		if (visitId) {
			dispatch(getVisitById({ visitId, pId: params.id }));
		} else {
			setSelectedVisit(null);
			formRef.current.setFieldValue(visitInitialValues);
		}
	}, [visitId]);

	useEffect(() => {
		setSelectedVisit(visitDetails);
	}, [visitDetails]);

	useEffect(() => {
		dispatch(getPastVisits({ pId: params.id, type: 'all', page: visitPage, per_page: perPage }));
	}, [visitPage]);

	return (
		<Container className='mt-4'>
			<Row>
				<Col className='d-flex justify-content-between'>
					<h5 className='text-primary'>
						{t(`VISIT.${params.visitId ? 'UPDATE_VISIT' : 'ADD_BUTTON'}`)}
					</h5>
					<div className='d-flex gap-2'>
						<Button size='sm' className='btn btn-danger btn-sm' onClick={handleEndConsultation} disabled={loading}>{t('VISIT.END_CONSULTATION')}</Button>
						<BackButton onBack={() => {
							formRef.current.setTouched({}, false);
							dispatch(resetVisitDetails());
							router(`/patients/visits/${params.id}`);
						}} />
						<Button size='sm' className='btn btn-primary btn-sm' onClick={() => {
							visitDetailsRef.current.scrollIntoView({ behavior: 'smooth' });
						}} >Load past visits</Button>
					</div>
				</Col>
			</Row>
			<hr />
			<Row className='mt-4'>
				{loading && (<Loader />)}
				<Col>
					<Card text='dark' bg='white'>

						<VisitHeader isDetails={false} handleLoadVisits={handleLoadVisits} handleClearAll={handleClearAll} />
						<VisitForm formRef={formRef} selectedVisit={selectedVisit} />
						<LoadVisitModal show={isLoadVisits} handleClose={() => setIsLoadVisits(false)} handleSelectVisit={handleSelectVisit} visits={allVisits} />

						<Container className='my-4' ref={visitDetailsRef}>
							<Card.Title className='text-primary'>{t('VISIT.PAST_VISITS')}</Card.Title>
							<hr />
							<RenderVisitList visit={pastVisits} total={totalPastVisits} currentPage={visitPage} onPageChange={setVisitPage} perPage={perPage} />
						</Container>
					</Card>
				</Col>
			</Row>
		</Container>
	);
};

export default AddUpdateVisit;
